// FILE: ProductList/generateProductCatalogPDF.js
// Generates a personalized product catalog PDF using jsPDF
// Design System: 4mm base grid, consistent spacing, visual hierarchy

import jsPDF from "jspdf";
import "jspdf-autotable";
import { getImageDataUrl } from "services/imageService";

// ─── Design Tokens ───────────────────────────────────────────────────────────
const SPACING = {
  xs: 1.5, // tight: between related micro-elements
  sm: 2.5, // small: between lines within a group
  md: 4, // medium: between groups
  lg: 6, // large: between sections
  xl: 8, // extra-large: major section gaps
};

// ─── Yield helper — lets the browser breathe between heavy draws ─────────────

function yieldToMain() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

// ─── Color Helpers ───────────────────────────────────────────────────────────

function hexToRGB(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 70, b: 67 };
}

function lightenColor(hex, percent) {
  const { r, g, b } = hexToRGB(hex);
  return {
    r: Math.min(255, r + (255 - r) * percent),
    g: Math.min(255, g + (255 - g) * percent),
    b: Math.min(255, b + (255 - b) * percent),
  };
}

// ─── Currency (Rs. — Helvetica doesn't support ₹) ──────────────────────────

function formatINR(amount) {
  if (!amount && amount !== 0) return "--";
  return (
    "Rs. " +
    Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// Short format for compact spaces
function formatINRShort(amount) {
  if (!amount && amount !== 0) return "--";
  return (
    "Rs. " +
    Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );
}

// ─── Address Builder (dedup) ─────────────────────────────────────────────────

function buildAddressLine(address) {
  if (!address) return "";
  const parts = [];
  if (address.line1) parts.push(address.line1.trim());
  if (address.line2) parts.push(address.line2.trim());
  if (
    address.city &&
    !parts.some((p) => p.toLowerCase().includes(address.city.toLowerCase()))
  ) {
    parts.push(address.city);
  }
  if (address.pinCode) {
    if (!parts.some((p) => p.includes(String(address.pinCode)))) {
      parts.push(String(address.pinCode));
    }
  }
  return parts.join(", ");
}

// ─── Image placeholder ──────────────────────────────────────────────────────

function drawImagePlaceholder(doc, x, y, w, h) {
  doc.setFillColor(247, 247, 247);
  doc.roundedRect(x, y, w, h, 1, 1, "F");
  doc.setFontSize(7);
  doc.setTextColor(190, 190, 190);
  doc.text("No Image", x + w / 2, y + h / 2 + 1, { align: "center" });
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═════════════════════════════════════════════════════════════════════════════

export async function generateProductCatalogPDF({
  products = [],
  branchDisplay = {},
  branchData = {},
  customerInfo = null,
  exportConfig = {},
  user = null,
  onProgress = null, // optional callback: (percent: number) => void
}) {
  const {
    showPricing = true,
    showStock = false,
    showBreakdown = false,
    layout = "catalog",
  } = exportConfig;

  // ── Branch display ───────────────────────────────────────────────────────
  const display = branchDisplay?.display || branchDisplay || {};
  const contactInfo =
    branchDisplay?.contactInfo || branchData?.contactInfo || {};
  const address = branchDisplay?.address || branchData?.address || {};

  const brandColors = display?.brandColors || {};
  const primaryColor = brandColors.primary || "#D4AF37";
  const secondaryColor = brandColors.secondary || "#1a1a2e";

  const storeName = display?.displayName || branchData?.name || "Jewelry Store";
  const tagline = display?.tagline || "";
  const logoUrl = display?.logo?.url || "";

  const primaryRGB = hexToRGB(primaryColor);
  const secondaryRGB = hexToRGB(secondaryColor);
  const lightPrimary = lightenColor(primaryColor, 0.85);
  const veryLightPrimary = lightenColor(primaryColor, 0.93);

  // ── Create PDF ───────────────────────────────────────────────────────────
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const pageWidth = doc.internal.pageSize.getWidth(); // 210
  const pageHeight = doc.internal.pageSize.getHeight(); // 297
  const margin = 12;
  const contentWidth = pageWidth - margin * 2; // 186
  let y = 0;

  // ── Progress helper ──────────────────────────────────────────────────────
  function reportProgress(percent) {
    if (onProgress) {
      try {
        onProgress(Math.round(percent));
      } catch (e) {
        /* ignore */
      }
    }
  }

  reportProgress(0);

  // ── Load logo via proxy ──────────────────────────────────────────────────
  let logoBase64 = null;
  if (logoUrl) {
    try {
      logoBase64 = await getImageDataUrl(logoUrl);
    } catch (e) {
      console.warn("Failed to load logo:", e);
    }
  }

  reportProgress(5);

  // ── Load product images via proxy (catalog layout) ──────────────────────
  const productImages = {};
  if (layout === "catalog") {
    const imagesToLoad = products.slice(0, 40).reduce((acc, product) => {
      const imgUrl = product.heroImageUrl || product.media?.[0]?.url;
      if (imgUrl) acc.push({ id: product._id, url: imgUrl });
      return acc;
    }, []);

    // Load in batches of 5 to avoid overwhelming the browser
    const BATCH_SIZE = 5;
    for (let i = 0; i < imagesToLoad.length; i += BATCH_SIZE) {
      const batch = imagesToLoad.slice(i, i + BATCH_SIZE);
      await Promise.allSettled(
        batch.map(async ({ id, url }) => {
          try {
            const b64 = await getImageDataUrl(url);
            if (b64) productImages[id] = b64;
          } catch (e) {
            console.warn("Failed to load product image:", id);
          }
        }),
      );
      // Yield between batches
      await yieldToMain();
      reportProgress(5 + ((i + BATCH_SIZE) / imagesToLoad.length) * 25);
    }
  }

  reportProgress(30);

  const addressLine = buildAddressLine(address);

  // ═══════════════════════════════════════════════════════════════════════════
  // HEADER — First Page (full) & Subsequent Pages (compact)
  // ═══════════════════════════════════════════════════════════════════════════

  function drawHeader(isFirstPage) {
    if (isFirstPage) {
      const mainH = 34;
      const addressStripH = addressLine ? 7 : 0;
      const accentH = 1;
      const darkH = mainH + addressStripH;
      const totalHeaderH = darkH + accentH;

      doc.setFillColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
      doc.rect(0, 0, pageWidth, darkH, "F");

      if (addressLine) {
        doc.setFillColor(
          Math.max(0, secondaryRGB.r + 8),
          Math.max(0, secondaryRGB.g + 8),
          Math.max(0, secondaryRGB.b + 8),
        );
        doc.rect(0, mainH, pageWidth, addressStripH, "F");

        doc.setDrawColor(
          Math.min(255, secondaryRGB.r + 25),
          Math.min(255, secondaryRGB.g + 25),
          Math.min(255, secondaryRGB.b + 25),
        );
        doc.setLineWidth(0.2);
        doc.line(margin, mainH, pageWidth - margin, mainH);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(5.5);
        doc.setTextColor(145, 145, 145);
        doc.text(addressLine, pageWidth / 2, mainH + addressStripH / 2 + 1, {
          align: "center",
          maxWidth: contentWidth - 10,
        });
      }

      doc.setFillColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
      doc.rect(0, darkH, pageWidth, accentH, "F");

      const logoSize = 16;
      const logoY = (mainH - logoSize) / 2;
      let textLeftX = margin;

      if (logoBase64) {
        try {
          doc.addImage(logoBase64, "JPEG", margin, logoY, logoSize, logoSize);
          textLeftX = margin + logoSize + SPACING.md;
        } catch (e) {
          /* skip */
        }
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(17);

      if (tagline) {
        const nameMetric = 5;
        const tagMetric = 2.5;
        const gap = 2;
        const blockH = nameMetric + gap + tagMetric;
        const blockTopY = (mainH - blockH) / 2;

        doc.setTextColor(255, 255, 255);
        doc.text(storeName, textLeftX, blockTopY + nameMetric);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
        doc.text(tagline, textLeftX, blockTopY + nameMetric + gap + tagMetric);
      } else {
        doc.setTextColor(255, 255, 255);
        doc.text(storeName, textLeftX, mainH / 2 + 2);
      }

      const contactItems = [];
      if (contactInfo?.phone?.primary)
        contactItems.push("Ph: " + contactInfo.phone.primary);
      if (contactInfo?.email?.primary)
        contactItems.push(contactInfo.email.primary);
      if (contactInfo?.website?.url) contactItems.push(contactInfo.website.url);
      if (contactInfo?.whatsapp?.number)
        contactItems.push("WhatsApp: " + contactInfo.whatsapp.number);

      if (contactItems.length > 0) {
        const cLineH = 3.8;
        const cBlockH = contactItems.length * cLineH;
        const cStartY = (mainH - cBlockH) / 2 + cLineH * 0.7;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(195, 195, 195);

        const rx = pageWidth - margin;
        contactItems.forEach((item, i) => {
          doc.text(item, rx, cStartY + i * cLineH, { align: "right" });
        });
      }

      y = totalHeaderH + SPACING.lg;

      if (customerInfo?.name) {
        const padV = 5;
        const padH = 6;
        const innerGap = 2;

        doc.setFontSize(6.5);
        const labelH = 2.5;
        doc.setFontSize(12);
        const nameH = 4;

        const boxH = padV + labelH + innerGap + nameH + padV;

        doc.setFillColor(
          Math.round(veryLightPrimary.r),
          Math.round(veryLightPrimary.g),
          Math.round(veryLightPrimary.b),
        );
        doc.roundedRect(margin, y, contentWidth, boxH, 2, 2, "F");

        doc.setFillColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
        doc.rect(margin, y + 3, 1.5, boxH - 6, "F");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(140, 140, 140);
        doc.text("Prepared exclusively for", margin + padH, y + padV + labelH);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
        doc.text(
          customerInfo.name,
          margin + padH,
          y + padV + labelH + innerGap + nameH,
        );

        if (customerInfo.phone || customerInfo.email) {
          const crx = pageWidth - margin - SPACING.md;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(6.5);
          doc.setTextColor(130, 130, 130);

          const lines = [];
          if (customerInfo.phone) lines.push(customerInfo.phone);
          if (customerInfo.email) lines.push(customerInfo.email);

          const lineH = 3.8;
          const blockH = lines.length * lineH;
          const startY = y + (boxH - blockH) / 2 + lineH * 0.6;

          lines.forEach((line, i) => {
            doc.text(line, crx, startY + i * lineH, { align: "right" });
          });
        }

        y += boxH + SPACING.md;
      }

      const titleBaseY = y + 4;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
      doc.text("Product Catalog", margin, titleBaseY);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(150, 150, 150);
      const dateStr = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      doc.text(
        dateStr +
          "  |  " +
          products.length +
          " product" +
          (products.length !== 1 ? "s" : ""),
        pageWidth - margin,
        titleBaseY,
        { align: "right" },
      );

      y = titleBaseY + 3;
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageWidth - margin, y);

      if (customerInfo?.note) {
        y += SPACING.md;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(7);
        doc.setTextColor(125, 125, 125);
        const noteLines = doc.splitTextToSize(
          "Note: " + customerInfo.note,
          contentWidth,
        );
        doc.text(noteLines.slice(0, 2), margin, y);
        y += noteLines.length * 3.5;
      }

      y += SPACING.lg;
    } else {
      const compactH = 16;

      doc.setFillColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
      doc.rect(0, 0, pageWidth, compactH, "F");

      doc.setFillColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
      doc.rect(0, compactH, pageWidth, 0.7, "F");

      let tx = margin;
      if (logoBase64) {
        try {
          const s = 8;
          doc.addImage(logoBase64, "JPEG", margin, (compactH - s) / 2, s, s);
          tx = margin + s + SPACING.sm;
        } catch (e) {
          /* skip */
        }
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);

      if (customerInfo?.name) {
        doc.text(storeName, tx, compactH / 2 - 0.5);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6);
        doc.setTextColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
        doc.text("For: " + customerInfo.name, tx, compactH / 2 + 3.5);
      } else {
        doc.text(storeName, tx, compactH / 2 + 1.5);
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(200, 200, 200);
      doc.text(
        "Page " + doc.internal.getNumberOfPages(),
        pageWidth - margin,
        compactH / 2 + 1.5,
        { align: "right" },
      );

      y = compactH + SPACING.md + 1;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FOOTER
  // ═══════════════════════════════════════════════════════════════════════════

  function drawFooter() {
    const fy = pageHeight - 7;

    doc.setFillColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
    doc.rect(margin, fy - 2, contentWidth, 0.3, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(4.5);
    doc.setTextColor(165, 165, 165);
    doc.text(
      storeName +
        "  |  Prices are indicative and subject to change based on prevailing market rates",
      margin,
      fy,
    );
    doc.text(
      "Generated on " + new Date().toLocaleString("en-IN"),
      pageWidth - margin,
      fy,
      { align: "right" },
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE BREAK HELPER
  // ═══════════════════════════════════════════════════════════════════════════

  function ensureSpace(needed) {
    if (y + needed > pageHeight - 12) {
      drawFooter();
      doc.addPage();
      drawHeader(false);
      return true;
    }
    return false;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT: CATALOG (now async — yields every 2 rows)
  // ═══════════════════════════════════════════════════════════════════════════

  async function renderCatalogLayout() {
    const primaryBranch = user?.branchAccess?.primaryBranch;
    const gap = SPACING.md;
    const colWidth = (contentWidth - gap) / 2;
    const cardPad = 3;

    const imgAreaW = 32;

    const sepOffset = imgAreaW + cardPad + 1.5;
    const textStartXOffset = sepOffset + 2.5;
    const textAvailW = colWidth - textStartXOffset - cardPad;

    function measureCardHeight(product) {
      const pricing = product.pricing?.branches?.[primaryBranch];
      let h = cardPad;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      const nameLines = doc.splitTextToSize(
        product.name || "Unnamed",
        textAvailW,
      );
      h += nameLines.length > 1 ? 8.5 : 5;
      h += SPACING.sm;
      h += 3;
      h += SPACING.xs;
      h += 3.5;

      if (product.metalDetails?.purity || product.purity) h += 3.2;
      if (product.hallmark?.isHallmarked) h += 4;
      h += 3.2;
      if (showStock) h += 3.5;

      if (showPricing && pricing?.available && pricing.breakdown?.finalAmount) {
        h += SPACING.sm + 1;
        h += SPACING.md;

        if (showBreakdown && pricing.breakdown) {
          let bdCount = 0;
          if (pricing.breakdown.metalValue) bdCount++;
          if (pricing.breakdown.makingCharges) bdCount++;
          if (pricing.breakdown.wastageCharges > 0) bdCount++;
          if (pricing.breakdown.gst?.amount) bdCount++;
          h += bdCount * 2.8;
          h += SPACING.xs;
        }
        h += 5;
      }

      h += cardPad;
      return Math.max(h, imgAreaW + cardPad * 2);
    }

    function drawCard(product, x, cardY, colW, cardH) {
      const pricing = product.pricing?.branches?.[primaryBranch];
      const textX = x + textStartXOffset;
      const rightEdge = x + colW - cardPad - 1;

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(215, 215, 215);
      doc.setLineWidth(0.25);
      doc.roundedRect(x, cardY, colW, cardH, 2, 2, "FD");

      const imgX = x + cardPad;
      const imgH = imgAreaW;
      const imgY = cardY + (cardH - imgH) / 2;

      doc.setFillColor(246, 246, 246);
      doc.roundedRect(imgX, imgY, imgAreaW, imgH, 1.5, 1.5, "F");

      if (productImages[product._id]) {
        try {
          doc.addImage(
            productImages[product._id],
            "JPEG",
            imgX + 0.5,
            imgY + 0.5,
            imgAreaW - 1,
            imgH - 1,
          );
        } catch (e) {
          drawImagePlaceholder(doc, imgX, imgY, imgAreaW, imgH);
        }
      } else {
        drawImagePlaceholder(doc, imgX, imgY, imgAreaW, imgH);
      }

      const sepX = x + sepOffset;
      doc.setDrawColor(232, 232, 232);
      doc.setLineWidth(0.15);
      doc.line(sepX, cardY + cardPad + 1, sepX, cardY + cardH - cardPad - 1);

      let ty = cardY + cardPad;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
      const nameLines = doc.splitTextToSize(
        product.name || "Unnamed",
        textAvailW,
      );
      doc.text(nameLines.slice(0, 2), textX, ty + 3);
      ty += nameLines.length > 1 ? 8.5 : 5;

      ty += SPACING.sm;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      doc.setTextColor(168, 168, 168);
      doc.text(product.sku || product.productId || "--", textX, ty);
      ty += 3;

      ty += SPACING.xs;
      doc.setFontSize(6.5);
      doc.setTextColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
      doc.setFont("helvetica", "bold");
      const typeStr =
        (product.type || "") +
        (product.subType ? "  |  " + product.subType : "");
      doc.text(typeStr, textX, ty);
      ty += 3.5;

      const purity = product.metalDetails?.purity || product.purity;
      if (purity) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6);
        doc.setTextColor(105, 105, 105);
        doc.text("Purity: " + purity, textX, ty);
        ty += 3.2;
      }

      if (product.hallmark?.isHallmarked) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5.5);
        const badgeText = "BIS Hallmarked";
        const badgeW = doc.getTextWidth(badgeText) + 3;
        doc.setFillColor(
          Math.round(lightenColor(primaryColor, 0.82).r),
          Math.round(lightenColor(primaryColor, 0.82).g),
          Math.round(lightenColor(primaryColor, 0.82).b),
        );
        doc.roundedRect(textX - 0.5, ty - 2.2, badgeW, 3.3, 0.6, 0.6, "F");
        doc.setTextColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
        doc.text(badgeText, textX + 1, ty);
        ty += 4;
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(115, 115, 115);
      doc.text(
        "Gross: " +
          (product.grossWeight || 0) +
          "g   Net: " +
          (product.netWeight || 0) +
          "g",
        textX,
        ty,
      );
      ty += 3.2;

      if (showStock) {
        const stk = product.stock?.availableQuantity || 0;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5.5);
        if (stk > 0) {
          doc.setTextColor(34, 139, 34);
          doc.text("In Stock: " + stk, textX, ty);
        } else {
          doc.setTextColor(200, 60, 60);
          doc.text("Out of Stock", textX, ty);
        }
        ty += 3.5;
      }

      if (showPricing && pricing?.available && pricing.breakdown?.finalAmount) {
        ty += SPACING.sm;

        doc.setDrawColor(225, 225, 225);
        doc.setLineWidth(0.15);
        doc.line(textX, ty, rightEdge, ty);
        ty += SPACING.md;

        if (showBreakdown && pricing.breakdown) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(5);

          const bdItems = [];
          if (pricing.breakdown.metalValue)
            bdItems.push([
              "Metal Value",
              formatINR(pricing.breakdown.metalValue),
            ]);
          if (pricing.breakdown.makingCharges)
            bdItems.push([
              "Making",
              formatINR(pricing.breakdown.makingCharges),
            ]);
          if (pricing.breakdown.wastageCharges > 0)
            bdItems.push([
              "Wastage",
              formatINR(pricing.breakdown.wastageCharges),
            ]);
          if (pricing.breakdown.gst?.amount)
            bdItems.push([
              "GST (" + pricing.breakdown.gst.rate + "%)",
              formatINR(pricing.breakdown.gst.amount),
            ]);

          bdItems.forEach((item) => {
            doc.setTextColor(155, 155, 155);
            doc.text(item[0], textX, ty);
            doc.setTextColor(100, 100, 100);
            doc.text(item[1], rightEdge, ty, { align: "right" });
            ty += 2.8;
          });

          ty += SPACING.xs;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
        doc.text(formatINR(pricing.breakdown.finalAmount), rightEdge, ty + 1, {
          align: "right",
        });
      }
    }

    // ── Render cards in pairs (rows) — yield every 2 rows ────────────
    const YIELD_EVERY_N_ROWS = 2;

    for (let i = 0; i < products.length; i += 2) {
      const left = products[i];
      const right = products[i + 1] || null;

      const leftH = measureCardHeight(left);
      const rightH = right ? measureCardHeight(right) : 0;
      const rowH = Math.max(leftH, rightH);

      ensureSpace(rowH + gap);

      drawCard(left, margin, y, colWidth, rowH);
      if (right) {
        drawCard(right, margin + colWidth + gap, y, colWidth, rowH);
      }

      y += rowH + gap;

      // Yield to main thread every N rows to keep browser responsive
      const rowIndex = i / 2;
      if (rowIndex > 0 && rowIndex % YIELD_EVERY_N_ROWS === 0) {
        await yieldToMain();
        reportProgress(30 + (i / products.length) * 60);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT: TABLE (autoTable is synchronous but generally fast)
  // ═══════════════════════════════════════════════════════════════════════════

  function renderTableLayout() {
    const primaryBranch = user?.branchAccess?.primaryBranch;

    const columns = [
      { header: "#", dataKey: "index" },
      { header: "Product", dataKey: "name" },
      { header: "SKU", dataKey: "sku" },
      { header: "Type", dataKey: "type" },
      { header: "Purity", dataKey: "purity" },
      { header: "Gross Wt", dataKey: "grossWeight" },
      { header: "Net Wt", dataKey: "netWeight" },
    ];
    if (showStock) columns.push({ header: "Stock", dataKey: "stock" });
    if (showPricing) {
      if (showBreakdown) {
        columns.push(
          { header: "Metal Value", dataKey: "metalValue" },
          { header: "Making", dataKey: "making" },
          { header: "GST", dataKey: "gst" },
        );
      }
      columns.push({ header: "Price", dataKey: "price" });
    }

    const rows = products.map((product, i) => {
      const pricing = product.pricing?.branches?.[primaryBranch];
      const row = {
        index: i + 1,
        name: product.name || "--",
        sku: product.sku || product.productId || "--",
        type:
          (product.type || "") +
          (product.subType ? "\n" + product.subType : ""),
        purity: product.metalDetails?.purity || product.purity || "--",
        grossWeight: (product.grossWeight || 0) + "g",
        netWeight: (product.netWeight || 0) + "g",
      };
      if (showStock) row.stock = String(product.stock?.availableQuantity ?? 0);
      if (showPricing && pricing?.available) {
        if (showBreakdown) {
          row.metalValue = formatINR(pricing.breakdown?.metalValue);
          row.making = formatINR(pricing.breakdown?.makingCharges);
          row.gst = formatINR(pricing.breakdown?.gst?.amount);
        }
        row.price = formatINR(pricing.breakdown?.finalAmount);
      } else if (showPricing) {
        row.price = "--";
        if (showBreakdown) {
          row.metalValue = "--";
          row.making = "--";
          row.gst = "--";
        }
      }
      return row;
    });

    doc.autoTable({
      columns,
      body: rows,
      startY: y,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 7,
        cellPadding: 2.5,
        lineColor: [220, 220, 220],
        lineWidth: 0.2,
        textColor: [50, 50, 50],
      },
      headStyles: {
        fillColor: [secondaryRGB.r, secondaryRGB.g, secondaryRGB.b],
        textColor: [255, 255, 255],
        fontSize: 7,
        fontStyle: "bold",
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [
          Math.round(veryLightPrimary.r),
          Math.round(veryLightPrimary.g),
          Math.round(veryLightPrimary.b),
        ],
      },
      columnStyles: {
        index: { cellWidth: 8, halign: "center" },
        name: { cellWidth: "auto", fontStyle: "bold" },
        sku: { cellWidth: 22, fontSize: 6 },
        price: {
          halign: "right",
          fontStyle: "bold",
          textColor: [primaryRGB.r, primaryRGB.g, primaryRGB.b],
        },
        metalValue: { halign: "right", fontSize: 6 },
        making: { halign: "right", fontSize: 6 },
        gst: { halign: "right", fontSize: 6 },
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) drawHeader(false);
        drawFooter();
      },
    });

    y = doc.lastAutoTable.finalY + SPACING.lg;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT: COMPACT (now async — yields every 15 rows)
  // ═══════════════════════════════════════════════════════════════════════════

  async function renderCompactLayout() {
    const primaryBranch = user?.branchAccess?.primaryBranch;

    const headerH = 7;
    doc.setFillColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
    doc.roundedRect(margin, y, contentWidth, headerH, 1, 1, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.setTextColor(255, 255, 255);
    const hBaseY = y + headerH / 2 + 1;
    doc.text("#", margin + 3, hBaseY);
    doc.text("Product Name", margin + 10, hBaseY);
    doc.text("Type / Purity", margin + 64, hBaseY);
    doc.text("Weight (G / N)", margin + 98, hBaseY);
    if (showPricing) {
      doc.text("Price", pageWidth - margin - 2, hBaseY, { align: "right" });
    }
    y += headerH + 1;

    const YIELD_EVERY_N_ROWS = 15;

    for (let index = 0; index < products.length; index++) {
      const product = products[index];
      const rowH = showBreakdown ? 14 : 8.5;
      ensureSpace(rowH + 1);

      const pricing = product.pricing?.branches?.[primaryBranch];

      if (index % 2 === 0) {
        doc.setFillColor(
          Math.round(veryLightPrimary.r),
          Math.round(veryLightPrimary.g),
          Math.round(veryLightPrimary.b),
        );
        doc.rect(margin, y - 1.5, contentWidth, rowH, "F");
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(170, 170, 170);
      doc.text(String(index + 1), margin + 3, y + 2);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
      const compactName = doc.splitTextToSize(product.name || "Unnamed", 48);
      doc.text(compactName[0], margin + 10, y + 2);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(110, 110, 110);
      doc.text(
        (product.type || "") +
          "  |  " +
          (product.metalDetails?.purity || product.purity || "--"),
        margin + 64,
        y + 2,
      );

      doc.text(
        (product.grossWeight || 0) + "g / " + (product.netWeight || 0) + "g",
        margin + 98,
        y + 2,
      );

      if (showPricing && pricing?.available && pricing.breakdown?.finalAmount) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
        doc.text(
          formatINR(pricing.breakdown.finalAmount),
          pageWidth - margin - 2,
          y + 2,
          { align: "right" },
        );
      }

      if (showBreakdown && pricing?.available && pricing.breakdown) {
        y += 4.5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(5);
        doc.setTextColor(145, 145, 145);
        const parts = [
          "Metal: " + formatINRShort(pricing.breakdown.metalValue),
          "Making: " + formatINRShort(pricing.breakdown.makingCharges),
          pricing.breakdown.wastageCharges > 0
            ? "Wastage: " + formatINRShort(pricing.breakdown.wastageCharges)
            : null,
          "GST: " + formatINRShort(pricing.breakdown.gst?.amount),
        ]
          .filter(Boolean)
          .join("  |  ");
        doc.text(parts, margin + 10, y + 2);
      }

      y += rowH;

      // Yield to main thread periodically
      if (index > 0 && index % YIELD_EVERY_N_ROWS === 0) {
        await yieldToMain();
        reportProgress(30 + (index / products.length) * 60);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SUMMARY
  // ═══════════════════════════════════════════════════════════════════════════

  function renderSummary() {
    const primaryBranch = user?.branchAccess?.primaryBranch;
    const pricedCount = products.filter(
      (p) => p.pricing?.branches?.[primaryBranch]?.breakdown?.finalAmount,
    ).length;
    const totalWeight = products.reduce(
      (sum, p) => sum + (p.grossWeight || 0),
      0,
    );

    ensureSpace(20);

    const barH = 14;

    doc.setFillColor(secondaryRGB.r, secondaryRGB.g, secondaryRGB.b);
    doc.roundedRect(margin, y, contentWidth, barH, 2, 2, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text("CATALOG SUMMARY", margin + 5, y + barH / 2 - 1);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(200, 200, 200);

    const statsY = y + barH / 2 + 3;
    doc.text("Total Products: " + products.length, margin + 5, statsY);
    doc.text(
      "Total Weight: " + totalWeight.toFixed(2) + "g",
      margin + 45,
      statsY,
    );
    if (showPricing && pricedCount > 0) {
      doc.text("Priced Items: " + pricedCount, margin + 90, statsY);
    }

    doc.setFontSize(5.5);
    doc.setTextColor(primaryRGB.r, primaryRGB.g, primaryRGB.b);
    doc.text("Prices valid for: " + storeName, pageWidth - margin - 4, statsY, {
      align: "right",
    });

    y += barH + SPACING.lg;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BUILD DOCUMENT
  // ═══════════════════════════════════════════════════════════════════════════

  drawHeader(true);

  switch (layout) {
    case "table":
      renderTableLayout();
      break;
    case "compact":
      await renderCompactLayout();
      break;
    case "catalog":
    default:
      await renderCatalogLayout();
      break;
  }

  reportProgress(92);
  await yieldToMain();

  renderSummary();
  drawFooter();

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter();
  }

  reportProgress(100);

  return doc.output("blob");
}

/**
 * Download the generated PDF
 */
export async function downloadProductCatalogPDF(options) {
  const blob = await generateProductCatalogPDF(options);

  const customerName = options.customerInfo?.name;
  const storeName =
    options.branchDisplay?.display?.displayName ||
    options.branchData?.name ||
    "Catalog";
  const dateStr = new Date().toISOString().split("T")[0];

  const fileName = customerName
    ? storeName +
      "_Catalog_" +
      customerName.replace(/\s+/g, "_") +
      "_" +
      dateStr +
      ".pdf"
    : storeName + "_Product_Catalog_" + dateStr + ".pdf";

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return fileName;
}

export default generateProductCatalogPDF;
